import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';

interface OnboardingState {
  step: string;
  version: number;
  workspaceName: string;
  websiteUrl: string | null;
  analyzeWebsite?: boolean;
  isComplete: boolean;
}

/**
 * BusinessSetup page (R5) — the business flow's first step.
 *
 * Collapses the former type/purpose/website steps into one screen:
 * choosing /start/business already established type=team and
 * purpose=business, so the only things left to capture are the workspace
 * name and an OPTIONAL website. Submitting advances to the outcomes step.
 *
 * The website field is genuinely optional — leaving it blank is an
 * explicit, first-class choice (no separate "skip" affordance needed) and
 * never dead-ends: enrichment is best-effort and the downstream review
 * step is conditional (R4).
 *
 * Website analysis is opt-in: the consent checkbox is unchecked by default
 * and disabled until a website is entered. Only a checked box sends
 * `analyzeWebsite: true`; otherwise Central makes no BrandTalk call.
 */
export function BusinessSetupPage() {
  const [workspaceName, setWorkspaceName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [analyzeWebsite, setAnalyzeWebsite] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  useEffect(() => {
    async function loadState() {
      try {
        const response = await fetch('/api/onboarding/state', { credentials: 'include' });
        const data = await response.json();
        if (response.ok && data.state) {
          const state = data.state as OnboardingState;
          if (state.workspaceName) setWorkspaceName(state.workspaceName);
          if (state.websiteUrl) {
            setWebsiteUrl(state.websiteUrl);
            setAnalyzeWebsite(state.analyzeWebsite === true);
          }
        }
      } catch {
        // Non-blocking — the user can still fill the form.
      } finally {
        setIsLoading(false);
      }
    }
    loadState();
  }, []);

  // The analysis opt-in unlocks only once a real-looking website is entered
  // (owner rule): a domain with a dot, optionally with http(s)://.
  const hasWebsite = /^(https?:\/\/)?[^\s/.]+(\.[^\s/.]+)+(\/\S*)?$/i.test(websiteUrl.trim());
  const analysisRequested = hasWebsite && analyzeWebsite;

  const handleWebsiteChange = (value: string) => {
    setWebsiteUrl(value);
    // No usable website → the opt-in is withdrawn and the box locks again.
    if (!/^(https?:\/\/)?[^\s/.]+(\.[^\s/.]+)+(\/\S*)?$/i.test(value.trim())) setAnalyzeWebsite(false);
  };

  const handleContinue = async () => {
    if (!workspaceName.trim()) return;

    setIsSaving(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          step: 'setup',
          data: {
            workspaceName: workspaceName.trim(),
            websiteUrl: websiteUrl.trim() || undefined,
            analyzeWebsite: analysisRequested,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save your workspace setup');
        return;
      }

      navigate(onboardingPath('business', 'outcomes', continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="setup" workspaceType="team" onboardingMode="business">
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </OnboardingLayout>
    );
  }

  const nextSteps = [
    'Choose the outcomes you want this workspace to improve',
    'Review the plan and pricing assembled from those outcomes',
    ...(analysisRequested ? ['Review suggestions we found from your website'] : []),
    'Invite your team and finish setup',
  ];

  return (
    <OnboardingLayout currentStep="setup" workspaceType="team" onboardingMode="business" wide>
      <div className="grid overflow-hidden rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 lg:grid-cols-5">
        <div className="p-4 sm:p-10 lg:col-span-3">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Business workspace</p>
          <h1 className="mt-2 text-xl sm:text-3xl font-semibold tracking-tight text-gray-900">Set up your business workspace</h1>
          <p className="mt-2 text-gray-500">
            A couple of quick details and we'll start assembling your workspace.
          </p>

          <div className="mt-8 space-y-6">
          {/* Workspace name */}
          <div>
            <label htmlFor="workspace-name" className="block text-sm font-medium text-gray-700 mb-2">
              Business / workspace name
            </label>
            <input
              id="workspace-name"
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="Acme Co"
              className="w-full px-4 py-3 bg-white text-gray-900 placeholder:text-gray-400 text-base border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-2">
              This is how your workspace appears across every app. You can change it later.
            </p>
          </div>

          {/* Optional website */}
          <div>
            <label htmlFor="website-url" className="block text-sm font-medium text-gray-700 mb-2">
              Website <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="website-url"
              type="url"
              value={websiteUrl}
              onChange={(e) => handleWebsiteChange(e.target.value)}
              placeholder="https://yourcompany.com"
              className="w-full px-4 py-3 bg-white text-gray-900 placeholder:text-gray-400 text-base border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-2">
              Skip it by leaving this blank — nothing downstream depends on it. We only analyze
              your site if you choose to below.
            </p>
            <label
              htmlFor="analyze-website"
              className={`mt-3 flex items-start gap-3 rounded-xl border p-3 text-sm transition-colors ${
                !hasWebsite
                  ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                  : analysisRequested
                  ? 'cursor-pointer border-emerald-300 bg-emerald-50/70 text-gray-800'
                  : 'cursor-pointer border-gray-200 bg-white text-gray-700 hover:border-emerald-200'
              }`}
            >
              <input
                id="analyze-website"
                type="checkbox"
                checked={analysisRequested}
                disabled={!hasWebsite}
                onChange={(e) => setAnalyzeWebsite(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:cursor-not-allowed"
              />
              <span>
                Analyze my website to pre-fill brand details, audiences and messaging (powered by
                LevelUp BrandTalk)
              </span>
            </label>
            {!hasWebsite && (
              <p className="mt-2 text-xs text-gray-500" data-testid="analyze-needs-website">
                Enter your website above to enable this.
              </p>
            )}
          </div>

          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-end gap-3 border-t border-gray-100 pt-6">
            <button
              type="button"
              onClick={handleContinue}
              disabled={isSaving || !workspaceName.trim()}
              className="px-5 py-2.5 sm:px-7 sm:py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-emerald-600/25"
            >
              {isSaving ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </div>
        <aside className="relative hidden lg:flex lg:col-span-2 flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 p-8 text-white">
          <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">What happens next</p>
            <ol className="mt-6 space-y-5">
              {nextSteps.map((item, i) => (
                <li key={item} className="flex gap-3">
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-emerald-200 ring-1 ring-white/15">
                    {i + 1}
                  </span>
                  <span className="pt-0.5 text-sm leading-relaxed text-slate-200">{item}</span>
                </li>
              ))}
            </ol>
          </div>
          <p className="relative mt-8 text-xs text-slate-400">Takes about a minute. You can change everything later.</p>
        </aside>
      </div>
    </OnboardingLayout>
  );
}
